import { useState } from 'react';
import type { Categoria, Producto } from '../api';
import { productoDisponibleHoy } from '../api';
import { useCart } from '../contexts/CartContext';

interface Props {
  categorias: Categoria[];
  productos: Producto[];
}

const DIA_LABEL: Record<string, string> = {
  lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves',
  viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo',
};

function etiquetaDias(dias?: string) {
  if (!dias) return null;
  return dias.split(',').map(d => DIA_LABEL[d.trim()] || d).join(' y ');
}

export default function Menu({ categorias, productos }: Props) {
  const [activa, setActiva] = useState<number | null>(null);
  const { addItem } = useCart();

  const catActiva = activa ?? categorias[0]?.id ?? null;
  const productosCategoria = productos.filter(p => p.categoria_id === catActiva);

  return (
    <div className="pt-32 pb-20 bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-xl mx-auto mb-12">
          <span className="text-accent font-bold text-xs tracking-[3px] uppercase mb-3 block">Nuestro Menú</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-wine-darker">Todo lo que ofrecemos</h1>
        </div>

        <div className="flex justify-center gap-2.5 mb-10 flex-wrap">
          {categorias.map(c => (
            <button
              key={c.id}
              onClick={() => setActiva(c.id)}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm border transition-colors ${
                catActiva === c.id
                  ? 'bg-wine text-white border-wine'
                  : 'bg-white text-wine border-cream-dark hover:bg-wine hover:text-white hover:border-wine'
              }`}
            >
              {c.icono} {c.nombre}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {productosCategoria.map(p => {
            const disponibleHoy = productoDisponibleHoy(p);
            return (
              <div key={p.id} className="bg-white rounded-2xl p-5 shadow-card flex gap-4 items-start">
                {p.imagen_url ? (
                  <img
                    src={p.imagen_url}
                    alt={p.nombre}
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-cream-dark flex items-center justify-center text-2xl flex-shrink-0">🍽️</div>
                )}
                <div className="flex-1">
                  <h4 className="font-display font-bold text-wine-darker text-[17px]">{p.nombre}</h4>
                  {p.descripcion && <p className="text-xs text-ink/50 mb-2">{p.descripcion}</p>}
                  {p.dias_disponibles && (
                    <p className={`text-[11px] font-semibold mb-2 ${disponibleHoy ? 'text-green-600' : 'text-accent'}`}>
                      {disponibleHoy ? '✓ Disponible hoy' : `Disponible: ${etiquetaDias(p.dias_disponibles)}`}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-wine text-[15px]">₡{p.precio.toLocaleString()}</span>
                    <button
                      onClick={() => addItem({ id: p.id, nombre: p.nombre, precio: p.precio })}
                      className="w-8 h-8 rounded-full bg-gold hover:bg-accent hover:text-white text-wine-darker font-bold flex items-center justify-center transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {productosCategoria.length === 0 && (
            <div className="col-span-full text-center text-ink/40 py-16">No hay productos en esta categoría todavía</div>
          )}
        </div>
      </div>
    </div>
  );
}
